import { SelectChangeEvent } from '@mui/material';
import _ from 'lodash';
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import {
    IFormFieldValidationConfig,
    IFormProps,
    isAlphanumericUppercase,
    isNotBlank,
    isShorterThanMaxLength,
    runFieldValidation,
    runFormValidation,
} from '../../CoreLib/library';
import { ContactDto, CustomerDto, QuickBooksProductDto, TermsCodeDto, UserDto } from '../../../dtos';
import { bBInvoiceTypes, countries, emptyGuid } from '../../../util';
import { AccountStatus } from '../../../dtos/enums/AccountStatus';

export interface ICustomerFormValues {
    name: string;
    customerNumber: string;
    addressLine1: string;
    addressLine2: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
    bbInvoiceType: string;
    pricing: string;
    miscCode: string;
    salesPersonId?: string;
    termsCodeId: string;
    isTaxable?: boolean;
    accountStatus: AccountStatus;
    accountCreditLimit?: number;
    accountComment: string;
    accountHighCreditDate?: Date;
    accountHighCreditAmount?: number;
    isActive: boolean;
    customerSince?: Date;
    accountsPayableContactId?: string;
    incomeTruckingProductId?: string;
    incomeMaterialsProductId?: string;
    incomeDumpProductId?: string;
    incomeMiscProductId?: string;
    incomeHourlyProductId?: string;
}

const DEFAULT_CUSTOMER: CustomerDto = {
    id: emptyGuid,
    name: '',
    customerNumber: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: 'IN',
    zipCode: '',
    country: countries[2], // US
    bbInvoiceType: bBInvoiceTypes[1], // Standard
    pricing: '',
    miscCode: '',
    salesPersonId: emptyGuid,
    termsCodeId: emptyGuid,
    isTaxable: false,
    accountStatus: AccountStatus.Normal,
    accountCreditLimit: undefined,
    accountComment: '',
    accountHighCreditDate: undefined,
    accountHighCreditAmount: undefined,
    isActive: true,
    createdOn: new Date(),
    clientId: emptyGuid,
    customerSince: undefined,
    accountsPayableContactId: emptyGuid,
    incomeTruckingProductId: emptyGuid,
    incomeMaterialsProductId: emptyGuid,
    incomeDumpProductId: emptyGuid,
    incomeMiscProductId: emptyGuid,
    incomeHourlyProductId: emptyGuid,
};

export function useCustomerForm(props: IFormProps<CustomerDto>, clearOnSave: boolean = false) {
    const { save, cancel, initValues } = props;
    const [formName, setFormName] = useState('');
    const [formCustomerNumber, setFormCustomerNumber] = useState('');
    const [formAddressLine1, setFormAddressLine1] = useState('');
    const [formAddressLine2, setFormAddressLine2] = useState('');
    const [formCity, setFormCity] = useState('');
    const [formState, setFormState] = useState('IN');
    const [formZipCode, setFormZipCode] = useState('');
    const [formCountry, setFormCountry] = useState(countries[2]);
    const [formBBInvoiceType, setFormBBInvoiceType] = useState(bBInvoiceTypes[1]);
    const [formPricing, setFormPricing] = useState('');
    const [formIsTaxable, setFormIsTaxable] = useState(initValues?.isTaxable ?? false);
    const [formMiscCode, setFormMiscCode] = useState('');
    const [formSalesPerson, setFormSalesPerson] = useState<UserDto | null>(null);
    const [formTermsCode, setFormTermsCode] = useState<TermsCodeDto | null>(null);
    const [formAccountStatus, setFormAccountStatus] = useState(AccountStatus.Normal);
    const [formAccountCreditLimit, setFormAccountCreditLimit] = useState<number>();
    const [formAccountComment, setFormAccountComment] = useState('');
    const [formAccountHighCreditDate, setFormAccountHighCreditDate] = useState<Date | null | undefined>(null);
    const [formAccountHighCreditAmount, setFormAccountHighCreditAmount] = useState<number>();
    const [formIsActive, setFormIsActive] = useState(true);
    const [isChanged, setIsChanged] = useState(false);
    const [formCustomerId, setFormCustomerId] = useState<string | undefined>(undefined);
    const [formCustomerSince, setFormCustomerSince] = useState<Date | null | undefined>(undefined);
    const [formAccountsPayableContactId, setFormAccountsPayableContactId] = useState<string | null>('');
    const [formIncomeTruckingProductId, setFormIncomeTruckingProductId] = useState<string | null>('');
    const [formIncomeMaterialsProductId, setFormIncomeMaterialsProductId] = useState<string | null>('');
    const [formIncomeDumpProductId, setFormIncomeDumpProductId] = useState<string | null>('');
    const [formIncomeMiscProductId, setFormIncomeMiscProductId] = useState<string | null>('');
    const [formIncomeHourlyProductId, setFormIncomeHourlyProductId] = useState<string | null>('');
    useEffect(() => {
        setFormName(initValues?.name ?? '');
        setFormCustomerNumber(initValues?.customerNumber ?? '');
        setFormAddressLine1(initValues?.addressLine1 ?? '');
        setFormAddressLine2(initValues?.addressLine2 ?? '');
        setFormCity(initValues?.city ?? '');
        setFormState(initValues?.state ?? 'IN');
        setFormZipCode(initValues?.zipCode ?? '');
        setFormCountry(initValues?.country ?? countries[2]);
        setFormBBInvoiceType(initValues?.bbInvoiceType ?? bBInvoiceTypes[1]);
        setFormPricing(initValues?.pricing ?? '');
        setFormMiscCode(initValues?.miscCode ?? '');
        setFormSalesPerson(initValues?.salesPerson ?? null);
        setFormTermsCode(initValues?.termsCode ?? null);
        setFormAccountStatus((initValues?.accountStatus as AccountStatus) ?? AccountStatus.Normal);
        setFormAccountCreditLimit(initValues?.accountCreditLimit);
        setFormAccountComment(initValues?.accountComment ?? '');
        setFormAccountHighCreditDate(initValues?.accountHighCreditDate ?? null);
        setFormAccountHighCreditAmount(initValues?.accountHighCreditAmount);
        setFormIsActive(initValues?.isActive ?? true);
        setFormIsTaxable(initValues?.isTaxable ?? false);
        setFormCustomerId(initValues?.id ?? undefined);
        setFormCustomerSince(initValues?.customerSince ? new Date(initValues.customerSince) : undefined);
        setFormAccountsPayableContactId(initValues?.accountsPayableContactId ?? null);
        setFormIncomeTruckingProductId(initValues?.incomeTruckingProductId ?? null);
        setFormIncomeMaterialsProductId(initValues?.incomeMaterialsProductId ?? null);
        setFormIncomeDumpProductId(initValues?.incomeDumpProductId ?? null);
        setFormIncomeMiscProductId(initValues?.incomeMiscProductId ?? null);
        setFormIncomeHourlyProductId(initValues?.incomeHourlyProductId ?? null);
    }, [initValues]);

    const [fieldErrors, setFieldErrors] = useState<Map<keyof CustomerDto, string>>(
        new Map([
            ['name', ''],
            ['customerNumber', ''],
            ['addressLine1', ''],
            ['city', ''],
            ['state', ''],
            ['zipCode', ''],
            ['country', ''],
            ['termsCodeId', ''],
        ])
    );

    const formFieldValidators = useMemo(() => new Map<keyof CustomerDto, IFormFieldValidationConfig>([
        [
            'name',
            {
                validators: [isNotBlank],
                errorMessageEntityName: 'Customer Name',
            },
        ],
        [
            'customerNumber',
            {
                validators: [isNotBlank, isShorterThanMaxLength(5), isAlphanumericUppercase()],
                errorMessageEntityName: 'Customer Number',
            },
        ],
        [
            'addressLine1',
            {
                validators: [isNotBlank],
                errorMessageEntityName: 'Address Line 1',
            },
        ],
        [
            'city',
            {
                validators: [isNotBlank],
                errorMessageEntityName: 'City',
            },
        ],
        [
            'state',
            {
                validators: [isNotBlank],
                errorMessageEntityName: 'State',
            },
        ],
        [
            'zipCode',
            {
                validators: [isNotBlank],
                errorMessageEntityName: 'Zip Code',
            },
        ],
        [
            'country',
            {
                validators: [isNotBlank],
                errorMessageEntityName: 'Country',
            },
        ],
        [
            'termsCodeId',
            {
                validators: [isNotBlank],
                errorMessageEntityName: 'Terms Code',
            },
        ],
    ]), []);

    const getCurrentFormValues = useCallback((): ICustomerFormValues => {
        return {
            name: formName,
            customerNumber: formCustomerNumber,
            addressLine1: formAddressLine1,
            addressLine2: formAddressLine2,
            city: formCity,
            state: formState,
            zipCode: formZipCode,
            country: formCountry,
            bbInvoiceType: formBBInvoiceType,
            pricing: formPricing,
            miscCode: formMiscCode,
            salesPersonId: formSalesPerson?.id,
            termsCodeId: formTermsCode?.id ?? '',
            isTaxable: formIsTaxable,
            accountStatus: formAccountStatus,
            accountCreditLimit: formAccountCreditLimit ?? undefined,
            accountComment: formAccountComment,
            accountHighCreditDate: formAccountHighCreditDate ?? undefined,
            accountHighCreditAmount: formAccountHighCreditAmount ?? undefined,
            isActive: formIsActive,
            customerSince: formCustomerSince ?? undefined,
            accountsPayableContactId: formAccountsPayableContactId ?? undefined,
            incomeTruckingProductId: formIncomeTruckingProductId ?? undefined,
            incomeMaterialsProductId: formIncomeMaterialsProductId ?? undefined,
            incomeDumpProductId: formIncomeDumpProductId ?? undefined,
            incomeMiscProductId: formIncomeMiscProductId ?? undefined,
            incomeHourlyProductId: formIncomeHourlyProductId ?? undefined,
        };
    }, [formIsTaxable, formName, formCustomerNumber, formAddressLine1, formAddressLine2, formCity, formState, formZipCode, formCountry, formBBInvoiceType, formPricing, formMiscCode, formSalesPerson, formTermsCode, formAccountStatus, formAccountCreditLimit, formAccountComment, formAccountHighCreditDate, formAccountHighCreditAmount, formIsActive, formCustomerSince, formAccountsPayableContactId, formIncomeTruckingProductId, formIncomeMaterialsProductId, formIncomeDumpProductId, formIncomeMiscProductId, formIncomeHourlyProductId]);

    const validateField = useCallback((fieldName: keyof ICustomerFormValues) => {
        const validationConfig = formFieldValidators.get(fieldName);
        if (validationConfig) {
            const formValues = getCurrentFormValues();
            const fieldValue = formValues[fieldName];
            const { errorMessage } = runFieldValidation(fieldValue, validationConfig);

            if (errorMessage !== fieldErrors.get(fieldName)) {
                const updatedFieldErrors = _.cloneDeep(fieldErrors);
                updatedFieldErrors.set(fieldName, errorMessage);
                setFieldErrors(updatedFieldErrors);
            }
        }
    }, [formFieldValidators, getCurrentFormValues, fieldErrors]);

    const validateForm = useCallback(() => {
        const formValues = getCurrentFormValues();
        const validationResult = runFormValidation<Partial<CustomerDto>>(formValues, formFieldValidators);
        setFieldErrors(validationResult.errorMessages);
        return validationResult.isValid;
    }, [formFieldValidators, getCurrentFormValues]);

    const isFormDirty = useCallback(() => {
        return isChanged;
    }, [isChanged]);

    const handleNameChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        setFormName(event.target.value);
        setIsChanged(true);
    }, [setFormName, setIsChanged]);

    const handleCustomerNumberChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        setFormCustomerNumber(event.target.value);
        setIsChanged(true);
    }, [setFormCustomerNumber, setIsChanged]);

    const handleAddressLine1Change = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        setFormAddressLine1(event.target.value);
        setIsChanged(true);
    }, [setFormAddressLine1, setIsChanged]);

    const handleAddressLine2Change = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        setFormAddressLine2(event.target.value);
        setIsChanged(true);
    }, [setFormAddressLine2, setIsChanged]);

    const handleCityChange = useCallback((event: SelectChangeEvent<string>) => {
        setFormCity(event.target.value);
        setIsChanged(true);
    }, [setFormCity, setIsChanged]);

    const handleStateChange = useCallback((event: SelectChangeEvent) => {
        setFormState(event.target.value);
        setIsChanged(true);
    }, [setFormState, setIsChanged]);

    const handleZipCodeChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        setFormZipCode(event.target.value);
        setIsChanged(true);
    }, [setFormZipCode, setIsChanged]);

    const handleCountryChange = useCallback((event: SelectChangeEvent) => {
        setFormCountry(event.target.value);
        setIsChanged(true);
    }, [setFormCountry, setIsChanged]);

    const handleBBInvoiceTypeChange = useCallback((event: SelectChangeEvent) => {
        setFormBBInvoiceType(event.target.value);
        setIsChanged(true);
    }, [setFormBBInvoiceType, setIsChanged]);

    const handlePricingChange = useCallback((event: SelectChangeEvent) => {
        setFormPricing(event.target.value);
        setIsChanged(true);
    }, [setFormPricing, setIsChanged]);

    const handleIsTaxableChange = useCallback((_: ChangeEvent<HTMLInputElement>, isChecked: boolean) => {
        setFormIsTaxable(isChecked);
        setIsChanged(true);
    }, [setFormIsTaxable, setIsChanged]);

    const handleMiscCodeChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        setFormMiscCode(event.target.value);
        setIsChanged(true);
    }, [setFormMiscCode, setIsChanged]);

    const handleSalesPersonChange = useCallback((value: any) => {
        setFormSalesPerson(value);
        setIsChanged(true);
    }, [setFormSalesPerson, setIsChanged]);

    const handleTermsCodeChange = useCallback((value: any) => {
        setFormTermsCode(value);
        setIsChanged(true);
    }, [setFormTermsCode, setIsChanged]);

    const handleAccountStatusChange = useCallback((event: SelectChangeEvent) => {
        setFormAccountStatus(event.target.value as AccountStatus);
        setIsChanged(true);
    }, [setFormAccountStatus, setIsChanged]);

    const handleAccountCreditLimitChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        setFormAccountCreditLimit(isNaN(event.target.valueAsNumber) ? undefined : event.target.valueAsNumber);
        setIsChanged(true);
    }, [setFormAccountCreditLimit, setIsChanged]);

    const handleAccountCommentChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        setFormAccountComment(event.target.value);
        setIsChanged(true);
    }, [setFormAccountComment, setIsChanged]);

    const handleAccountHighCreditDateChange = useCallback((value: Date | null | undefined) => {
        setFormAccountHighCreditDate(value);
        setIsChanged(true);
    }, [setFormAccountHighCreditDate, setIsChanged]);

    const handleAccountHighCreditAmountChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        setFormAccountHighCreditAmount(isNaN(event.target.valueAsNumber) ? undefined : event.target.valueAsNumber);
        setIsChanged(true);
    }, [setFormAccountHighCreditAmount, setIsChanged]);

    const handleIsActiveChange = useCallback((_: ChangeEvent<HTMLInputElement>, isChecked: boolean) => {
        setFormIsActive(isChecked);
        setIsChanged(true);
    }, [setFormIsActive, setIsChanged]);

    const handleCustomerSinceChange = useCallback((date: Date | null | undefined) => {
        setFormCustomerSince(date);
        setIsChanged(true);
    }, [setFormCustomerSince, setIsChanged]);

    const handleAccountsPayableContactChange = useCallback((selectedContact?: ContactDto) => {
        setFormAccountsPayableContactId(selectedContact?.id ?? null);
        setIsChanged(true);
    }, [setFormAccountsPayableContactId, setIsChanged]);

    const handleIncomeTruckingProductChange = useCallback((selectedProduct?: QuickBooksProductDto) => {
        setFormIncomeTruckingProductId(selectedProduct?.id ?? null);
        setIsChanged(true);
    }, [setFormIncomeTruckingProductId, setIsChanged]);

    const handleIncomeMaterialsProductChange = useCallback((selectedProduct?: QuickBooksProductDto) => {
        setFormIncomeMaterialsProductId(selectedProduct?.id ?? null);
        setIsChanged(true);
    }, [setFormIncomeMaterialsProductId, setIsChanged]);

    const handleIncomeDumpProductChange = useCallback((selectedProduct?: QuickBooksProductDto) => {
        setFormIncomeDumpProductId(selectedProduct?.id ?? null);
        setIsChanged(true);
    }, [setFormIncomeDumpProductId, setIsChanged]);

    const handleIncomeMiscProductChange = useCallback((selectedProduct?: QuickBooksProductDto) => {
        setFormIncomeMiscProductId(selectedProduct?.id ?? null);
        setIsChanged(true);
    }, [setFormIncomeMiscProductId, setIsChanged]);

    const handleIncomeHourlyProductChange = useCallback((selectedProduct?: QuickBooksProductDto) => {
        setFormIncomeHourlyProductId(selectedProduct?.id ?? null);
        setIsChanged(true);
    }, [setFormIncomeHourlyProductId, setIsChanged]);

    const clearForm = useCallback(() => {
        setFormName('');
        setFormCustomerNumber('');
        setFormAddressLine1('');
        setFormAddressLine2('');
        setFormCity('');
        setFormState('IN');
        setFormZipCode('');
        setFormCountry(countries[2]);
        setFormBBInvoiceType(bBInvoiceTypes[1]);
        setFormPricing('');
        setFormIsTaxable(false);
        setFormMiscCode('');
        setFormSalesPerson(null);
        setFormTermsCode(null);
        setFormAccountStatus(AccountStatus.Normal);
        setFormAccountCreditLimit(undefined);
        setFormAccountComment('');
        setFormAccountHighCreditDate(null);
        setFormAccountHighCreditAmount(undefined);
        setFormIsActive(true);
        setFormCustomerSince(new Date());
        setFormAccountsPayableContactId(emptyGuid);
        setFormIncomeTruckingProductId(null);
        setFormIncomeMaterialsProductId(null);
        setFormIncomeDumpProductId(null);
        setFormIncomeMiscProductId(null);
        setFormIncomeHourlyProductId(null);
        }, []);

    const handleSave = useCallback(() => {
        const isFormValid = validateForm();
        if (isFormValid) {
            const formValues = getCurrentFormValues();
            const updatedUserRecord = {
                ...(initValues ?? DEFAULT_CUSTOMER),
                ...formValues,
            };
            save(updatedUserRecord);
            if (clearOnSave) {
                clearForm();
            }
            setIsChanged(false);
        }
    }, [clearOnSave, clearForm, initValues, save, validateForm, getCurrentFormValues]);

    const handleCancel = useCallback(() => {
        cancel();
        if (clearOnSave) {
            clearForm();
        }
        setIsChanged(false);
    }, [clearOnSave, clearForm, cancel]);

    return {
        isFormDirty,
        handleSave,
        handleCancel,
        fieldErrors,
        formIsActive,
        formName,
        formCustomerNumber,
        formAddressLine1,
        formAddressLine2,
        formCity,
        formState,
        formZipCode,
        formCountry,
        formBBInvoiceType,
        formPricing,
        formIsTaxable,
        formMiscCode,
        formSalesPerson,
        formTermsCode,
        formAccountStatus,
        formAccountCreditLimit,
        formAccountComment,
        formAccountHighCreditDate,
        formAccountHighCreditAmount,
        formCustomerId,
        formCustomerSince,
        formAccountsPayableContactId,
        handleNameChange,
        handleCustomerNumberChange,
        handleAddressLine1Change,
        handleAddressLine2Change,
        handleCityChange,
        handleStateChange,
        handleZipCodeChange,
        handleCountryChange,
        handleBBInvoiceTypeChange,
        handlePricingChange,
        handleIsTaxableChange,
        handleMiscCodeChange,
        handleSalesPersonChange,
        handleTermsCodeChange,
        handleAccountStatusChange,
        handleAccountCreditLimitChange,
        handleAccountCommentChange,
        handleAccountHighCreditDateChange,
        handleAccountHighCreditAmountChange,
        handleIsActiveChange,
        handleCustomerSinceChange,
        handleAccountsPayableContactChange,
        validateField,
        setFormAddressLine1,
        setFormCity,
        setFormCountry,
        setFormState,
        setFormZipCode,
        handleIncomeTruckingProductChange,
        handleIncomeMaterialsProductChange,
        handleIncomeDumpProductChange,
        handleIncomeMiscProductChange,
        handleIncomeHourlyProductChange,
        formIncomeTruckingProductId,
        formIncomeMaterialsProductId,
        formIncomeDumpProductId,
        formIncomeMiscProductId,
        formIncomeHourlyProductId,
    };
}

export type CustomerFormManager = ReturnType<typeof useCustomerForm>;
